import { Box, Button, Paper, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';

interface ErrorPageProps {
  error?: Error | null;
  resetError?: () => void;
}

export const ErrorPage = ({ error, resetError }: ErrorPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleGoBack = () => {
    if (resetError) {
      resetError();
    }
    // Navigate to previous page or home
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleRestart = () => {
    // Reload the application
    window.location.reload();
  };

  const errorMessage =
    error?.message ||
    'An unexpected error occurred. Please try again or contact support if the problem persists.';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        p: 3,
      }}
    >
      <Paper
        sx={{
          p: 4,
          maxWidth: 600,
          textAlign: 'center',
        }}
        elevation={3}
      >
        <ErrorOutlineIcon
          sx={{ fontSize: 64, color: 'error.main', mb: 2 }}
        />
        <Typography variant="h4" gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {errorMessage}
        </Typography>
        {error?.stack && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              textAlign: 'left',
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            <Typography
              variant="caption"
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {error.stack}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
          >
            Go Back
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRestart}
          >
            Restart Application
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

